/**
 * WHF Gallery — true masonry layout + image lightbox.
 *
 * Masonry: lays figures out left-to-right across CSS-grid columns, sizing each
 * item's row span from its measured height so variable-height images tile with
 * no left-clumping (the old CSS `columns:` filled top-to-bottom).
 *
 * Lightbox: clicking any [data-lightbox-src] opens an accessible overlay with
 * prev/next within its [data-lightbox] group, keyboard + backdrop close.
 */

const CLOSE_SVG =
	'<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const ARROW_SVG =
	'<svg width="20" height="20" viewBox="0 0 8 12" fill="none"><path d="M1.2364 0L0 1.213L4.559 5.713L0 10.213L1.2364 11.427L7.058 5.713L1.2364 0Z" fill="currentColor"/></svg>';

// -------------------------------------------------------------------------
// Masonry (editor-controlled bento grid) — a fixed N-column grid where each
// image spans the columns/rows the editor chose. JS only keeps the row unit
// equal to the column width so a 1×1 cell stays square; everything else is
// CSS grid (grid-auto-flow: dense packs the chosen spans).
// -------------------------------------------------------------------------
function initMasonry() {
	const grids = document.querySelectorAll( '[data-whf-masonry]' );
	if ( ! grids.length ) return;
	grids.forEach( setupBento );
}

function setupBento( grid ) {
	const rootPx =
		parseFloat( getComputedStyle( document.documentElement ).fontSize ) || 16;

	const squareRows = () => {
		const cs = getComputedStyle( grid );
		const cols =
			parseInt( cs.getPropertyValue( '--whf-masonry-cols' ), 10 ) || 3;
		const gapRaw = cs.getPropertyValue( '--whf-masonry-gap' ).trim();
		const gap = gapRaw.endsWith( 'rem' )
			? parseFloat( gapRaw ) * rootPx
			: parseFloat( gapRaw ) || 16;

		const colWidth = ( grid.clientWidth - gap * ( cols - 1 ) ) / cols;
		grid.style.setProperty( '--whf-masonry-row', Math.max( 0, colWidth ) + 'px' );
	};

	squareRows();

	let t = null;
	window.addEventListener(
		'resize',
		() => {
			clearTimeout( t );
			t = setTimeout( squareRows, 150 );
		},
		{ passive: true }
	);
}

// -------------------------------------------------------------------------
// Lightbox
// -------------------------------------------------------------------------
function initLightbox() {
	const triggers = Array.from(
		document.querySelectorAll( '[data-lightbox-src]' )
	);
	if ( ! triggers.length ) return;

	// Group triggers by their [data-lightbox] value for prev/next.
	const groups = new Map();
	triggers.forEach( ( el ) => {
		const g = el.getAttribute( 'data-lightbox' ) || 'default';
		if ( ! groups.has( g ) ) groups.set( g, [] );
		groups.get( g ).push( el );
	} );

	const overlay = document.createElement( 'div' );
	overlay.className = 'whf-lightbox';
	overlay.setAttribute( 'hidden', '' );
	overlay.innerHTML =
		'<div class="whf-lightbox__backdrop" data-lb-close></div>' +
		'<div class="whf-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Image viewer">' +
		'<button class="whf-lightbox__close" type="button" data-lb-close aria-label="Close">' + CLOSE_SVG + '</button>' +
		'<button class="whf-lightbox__nav whf-lightbox__nav--prev" type="button" aria-label="Previous image">' + ARROW_SVG + '</button>' +
		'<figure class="whf-lightbox__figure"><img class="whf-lightbox__img" src="" alt=""><figcaption class="whf-lightbox__caption"></figcaption></figure>' +
		'<button class="whf-lightbox__nav whf-lightbox__nav--next" type="button" aria-label="Next image">' + ARROW_SVG + '</button>' +
		'</div>';
	document.body.appendChild( overlay );

	const imgEl = overlay.querySelector( '.whf-lightbox__img' );
	const capEl = overlay.querySelector( '.whf-lightbox__caption' );
	const prevBtn = overlay.querySelector( '.whf-lightbox__nav--prev' );
	const nextBtn = overlay.querySelector( '.whf-lightbox__nav--next' );
	const closeBtn = overlay.querySelector( '.whf-lightbox__close' );

	let current = [];
	let index = 0;
	let lastFocus = null;

	const show = () => {
		const el = current[ index ];
		const caption = el.getAttribute( 'data-lightbox-caption' ) || '';
		imgEl.src = el.getAttribute( 'data-lightbox-src' );
		imgEl.alt = caption;
		capEl.textContent = caption;
		capEl.hidden = ! caption;
		const multi = current.length > 1;
		prevBtn.hidden = ! multi;
		nextBtn.hidden = ! multi;
	};

	const open = ( groupName, startIndex ) => {
		current = groups.get( groupName ) || [];
		if ( ! current.length ) return;
		index = Math.max( 0, startIndex );
		lastFocus = document.activeElement;
		show();
		overlay.removeAttribute( 'hidden' );
		document.documentElement.classList.add( 'whf-lightbox-open' );
		closeBtn.focus();
	};

	const close = () => {
		overlay.setAttribute( 'hidden', '' );
		document.documentElement.classList.remove( 'whf-lightbox-open' );
		imgEl.src = '';
		if ( lastFocus && lastFocus.focus ) lastFocus.focus();
	};

	const go = ( dir ) => {
		index = ( index + dir + current.length ) % current.length;
		show();
	};

	triggers.forEach( ( el ) => {
		el.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			const g = el.getAttribute( 'data-lightbox' ) || 'default';
			open( g, ( groups.get( g ) || [] ).indexOf( el ) );
		} );
	} );

	overlay.addEventListener( 'click', ( e ) => {
		if ( e.target.hasAttribute( 'data-lb-close' ) ) close();
	} );
	prevBtn.addEventListener( 'click', () => go( -1 ) );
	nextBtn.addEventListener( 'click', () => go( 1 ) );

	document.addEventListener( 'keydown', ( e ) => {
		if ( overlay.hasAttribute( 'hidden' ) ) return;
		if ( 'Escape' === e.key ) {
			close();
		} else if ( 'ArrowLeft' === e.key ) {
			go( -1 );
		} else if ( 'ArrowRight' === e.key ) {
			go( 1 );
		} else if ( 'Tab' === e.key ) {
			// Trap focus inside the dialog (aria-modal): cycle among the visible
			// controls so Tab can't reach the page behind the overlay.
			const focusable = [ closeBtn, prevBtn, nextBtn ].filter( ( b ) => ! b.hidden );
			if ( ! focusable.length ) return;
			const first = focusable[ 0 ];
			const last = focusable[ focusable.length - 1 ];
			if ( e.shiftKey && document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			} else if ( ! e.shiftKey && document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		}
	} );
}

export function initGallery() {
	initMasonry();
	initLightbox();
}
